import { Injectable, Signal, WritableSignal, computed, inject, signal } from '@angular/core';
import {
  CollectionReference,
  DocumentData,
  Firestore,
  QueryConstraint,
  QueryDocumentSnapshot,
  addDoc,
  collection,
  collectionData,
  collectionSnapshots,
  deleteDoc,
  doc,
  getDoc,
  query,
  updateDoc,
  where,
} from '@angular/fire/firestore';
import dayjs from 'dayjs';
import { Observable, Subscription, catchError, map, of } from 'rxjs';
import { generateReport } from './assessments.functions';
import { generateChartReport, generateChartReportCustom } from './assessmentsHistory.functions';
import { CustomAssessmentsService } from './custom-assessments.service';
import { LogService } from './log.service';
import {
  Assessment,
  AssessmentChartDataSetCustom,
  AssessmentChartDataSets,
  CustomAssessment,
  IcfCategory,
  ReportItem,
  ReportItemNote,
} from './model';
import { PatientsService } from './patients.service';

@Injectable({
  providedIn: 'root',
})
export class AssessmentsService {
  patientService = inject(PatientsService);
  customAssessmentsService = inject(CustomAssessmentsService);
  firestore: Firestore = inject(Firestore);
  assessmentList: WritableSignal<Assessment[]> = signal<Assessment[]>([]);
  reportNotesList: WritableSignal<ReportItemNote[]> = signal<ReportItemNote[]>([]);
  reportNotesLastUpdatedTime: Signal<number> = computed(() => {
    const lastUpdatedDate = this.reportNotesList().reduce((latest, item) => {
      if (!item.dateIso) return latest;
      const itemDate = dayjs(item.dateIso).toDate().getTime();
      return itemDate > latest ? itemDate : latest;
    }, 0);
    // console.log(
    //   'reportNotesLastUpdatedTime',
    //   dayjs(lastUpdatedDate).format('YYYY-MM-DD HH:mm:ss'),
    //   this.reportNotesList()
    // );

    return lastUpdatedDate;
  });
  reportList: WritableSignal<ReportItem[] | undefined> = signal<ReportItem[] | undefined>(undefined);

  // merke: assessmentChartDataSets muss auch die custom elemente enthalten, damit der chart für die Zeitachse alle Datumse zur Verfügung hat!
  assessmentChartDataSets: WritableSignal<AssessmentChartDataSets | undefined> = signal<
    AssessmentChartDataSets | undefined
  >(undefined);
  assessmentChartDataSetsCustom: WritableSignal<AssessmentChartDataSetCustom[]> = signal<
    AssessmentChartDataSetCustom[]
  >([]);

  customAssessmentList: Signal<CustomAssessment[]> = this.customAssessmentsService.customAssessmentList;

  collectionName = computed(() => {
    return this.patientService.currentPatientPath() + '/assessments';
  });

  collectionNameReportNotes = computed(() => {
    return this.patientService.currentPatientPath() + '/reportnotes';
  });

  lastTimestamp: number = 0;
  collectionSubscription: Subscription | undefined;
  collectionSubIsForName: string = '';

  private logger: LogService = inject(LogService);

  getAssessments() {
    console.log('AssessmentsService.getAssessments collectionName(): ' + this.collectionName());
    if (this.collectionName() !== this.collectionSubIsForName) {
      this.collectionSubscription?.unsubscribe();
      this.collectionSubscription = undefined;
      this.collectionSubIsForName = this.collectionName();
    }
    if (this.collectionSubscription === undefined) {
      /* fetch only once */

      // fetch CustomAssessments once
      this.customAssessmentsService.getCustomAssessments();

      // fetch Assessments once
      const assessmentsCollection = collection(this.firestore, this.collectionName());
      let collectionData$: Observable<QueryDocumentSnapshot<DocumentData, DocumentData>[]>;
      try {
        collectionData$ = collectionSnapshots(assessmentsCollection);
        this.collectionSubscription = collectionData$
          .pipe(
            map(result => {
              return result.map(snapshot => {
                const assessment: Assessment = { ...(snapshot.data() as Assessment), _documentId: snapshot.id };
                return assessment;
              });
            }),
            catchError((_error: any) => {
              const error = _error.code;
              console.log('error aaa', { error });
              if (error === 'permission-denied') {
                console.log('loggedout???');
              } else {
                this.logger.error('error in getAssessments', { error });
              }
              return of([]);
            })
          )
          .subscribe((assessments: Assessment[]) => {
            const now = Date.now();
            const delta = now - this.lastTimestamp;
            this.lastTimestamp = now;
            this.logger.log('Assessments ' + assessments.length + ' for ' + location.href);
            console.log('############ getAssessments result ' + delta + ' ms after last one', assessments);
            const sortedAssessments: Assessment[] = assessments.sort(
              (a, b) => new Date(b.dateIso).getTime() - new Date(a.dateIso).getTime()
            );
            console.log('set assessmentList signal sortedAssessments', sortedAssessments);
            this.assessmentList.set(sortedAssessments);

            this.generateReport(sortedAssessments);
            this.generateChartReport(sortedAssessments);
          });
      } catch (error) {
        console.log('error22', error);
      }

      // fetch ReportNotes only once too!
      this.getReportNotes();
    }
  }
  private getReportNotes() {
    console.log('AssessmentsService.getReportNotes collectionName(): ' + this.collectionNameReportNotes());

    const reportNotesCollection = collection(this.firestore, this.collectionNameReportNotes());
    const data$: Observable<QueryDocumentSnapshot<DocumentData, DocumentData>[]> =
      collectionSnapshots(reportNotesCollection);
    data$
      .pipe(
        map(result => {
          return result.map(snapshot => {
            const reportItemNote: ReportItemNote = { ...(snapshot.data() as ReportItemNote), _documentId: snapshot.id };
            return reportItemNote;
          });
        }),
        catchError((_error: any) => {
          const error = _error.code;
          console.log('error aaa', { error });
          if (error === 'permission-denied') {
            console.log('report notes müssen noch erzeugt werden');
          } else {
            this.logger.error('error in getReportNotes', { error });
          }
          return of([]);
        })
      )
      .subscribe((reportItemNotes: ReportItemNote[]) => {
        console.log('reportItemNotes result', reportItemNotes);
        const sortedReportItemNotes: ReportItemNote[] = reportItemNotes.sort((a, b) =>
          a.icfCategory.localeCompare(b.icfCategory)
        );
        console.log('set reportNotesList signal sortedReportItemNotes', sortedReportItemNotes);
        this.reportNotesList.set(sortedReportItemNotes);

        // now recreate report
        const sortedAssessments = this.assessmentList();
        this.generateReport(sortedAssessments);
        this.generateChartReport(sortedAssessments);
      });
  }

  // by icf code
  generateReport(assessments: Assessment[]) {
    const reportItems: { [key in IcfCategory]?: ReportItem } = generateReport(assessments, this.reportNotesList());

    console.log('reportItems', reportItems);
    const sortedReportItems = Object.values(reportItems).sort((a, b) => a.icfCategory.localeCompare(b.icfCategory));

    const lastIcfUpdateTime: string | undefined = this.patientService.patient()?.lastIcfUpdateTime;
    let lastUpdatedTime: number;

    if (lastIcfUpdateTime === undefined) {
      // Zeitpunkt wurde noch nicht gesetzt (user hat noch nie bestätigt). Um herauszufinden, ob der User schonmal einen ICF-Score gesetzt hat:
      const lastReportNotesUpdatedTime = this.reportNotesLastUpdatedTime();
      // ... und dann nur für diesen fall als ausgangspunkt nehmen:
      lastUpdatedTime = lastReportNotesUpdatedTime;
      console.log('lastUpdatedTime1', {
        patient: this.patientService.patient()?.lastIcfUpdateTime,
        lastUpdatedTime: dayjs(lastUpdatedTime).format('YYYY-MM-DD HH:mm:ss'),
        lastReportNotesUpdatedTime: dayjs(lastReportNotesUpdatedTime).format('YYYY-MM-DD HH:mm:ss'),
        lastIcfUpdateTime:
          lastIcfUpdateTime == undefined ? undefined : dayjs(lastIcfUpdateTime).format('YYYY-MM-DD HH:mm:ss'),
      });
    } else {
      // ansonsten gilt immer der Zeitpunkt wann der user zuletzt BESTÄTIGT hat:
      lastUpdatedTime = dayjs(lastIcfUpdateTime).toDate().getTime();
    }

    if (reportItems && lastUpdatedTime > 0) {
      sortedReportItems.forEach(item => {
        // console.log('details ', item.details, item.assessmentTypes);

        item.isAssessmentLaterWarning = item.details?.some(detail => {
          const isAssessmentLater = dayjs(detail.dateIso).toDate().getTime() >= lastUpdatedTime;
          console.log('isAssessmentLater', {
            date: dayjs(detail.dateIso).format('YYYY-MM-DD HH:mm:ss'),
            isAssessmentLater,
          });
          return isAssessmentLater;
        });
      });
    }

    this.reportList.set(sortedReportItems);
  }

  // by assessment with history data
  generateChartReport(assessments: Assessment[]) {
    const chartDataSets: AssessmentChartDataSets = generateChartReport(assessments);
    const chartDataSetsCustom: AssessmentChartDataSetCustom[] = generateChartReportCustom(
      assessments,
      this.customAssessmentList()
    );

    console.log('chartDataSets', chartDataSets);
    this.assessmentChartDataSets.set(chartDataSets);
    this.assessmentChartDataSetsCustom.set(chartDataSetsCustom);
  }

  async getAssessment(documentId: string): Promise<Assessment> {
    // console.log('getAssessment ', this.collectionName() + documentId);
    const docRef = doc(this.firestore, this.collectionName(), documentId);
    const snapshot = await getDoc(docRef);
    // console.log('getAssessment res ', snapshot.data());
    const assessment: Assessment = { ...(snapshot.data() as Assessment), _documentId: snapshot.id };
    console.log('getAssessment res ', assessment);
    return assessment;
  }

  createAssessment(assessment: Assessment) {
    this.logger.log('createAssessment ', assessment);
    console.log('createAssessment AssessmentsService.collectionName(): ' + this.collectionName(), assessment);
    return addDoc(collection(this.firestore, this.collectionName()), assessment);
  }

  updateAssessment(documentId: string, assessment: Assessment) {
    this.logger.log('updateAssessment ', assessment);
    console.log('updateAssessment AssessmentsService.collectionName(): ' + this.collectionName(), assessment);
    const docRef = doc(this.firestore, this.collectionName(), documentId!);
    return updateDoc(docRef, assessment).then(result => {
      console.log('updateAssessment result', result);
    });
  }

  deleteAssessment(documentId: string) {
    this.logger.log('deleteAssessment ', { documentId });
    const docRef = doc(this.firestore, this.collectionName(), documentId);
    return deleteDoc(docRef).then(result => {
      console.log('deleteAssessment result', result);
    });
  }

  // Report notes

  createOrUpdateReportNote(item: Partial<ReportItemNote>) {
    console.log('AssessmentsService.createReportNote(): ' + this.collectionNameReportNotes(), item);
    const reportNotesDetailList: ReportItemNote[] = this.reportNotesList();
    const details = reportNotesDetailList.find(details => details.icfCategory === item.icfCategory);
    if (details?._documentId) {
      if (item.note !== undefined) {
        // merge note into details
        details.note = item.note;
      }
      if (item.icfScoreSelected !== undefined) {
        // merge icfScoreSelected into details
        details.icfScoreSelected = item.icfScoreSelected;
      }
      details.dateIso = dayjs().format('YYYY-MM-DD HH:mm:ss');
      console.log('update note ' + details._documentId);
      const docRef = doc(this.firestore, this.collectionNameReportNotes(), details._documentId);
      return updateDoc(docRef, details).then(result => {
        console.log('updateReportNote result', result);
      });
    } else {
      item.dateIso = dayjs().format('YYYY-MM-DD HH:mm:ss');
      console.log('create note for ', item);
      return addDoc(collection(this.firestore, this.collectionNameReportNotes()), item).then(result => {
        console.log('createReportNote result', result);
        if (this.reportNotesList().length === 0) {
          // retrigger subscription, cause not working before?
        }
      });
    }
  }

  getAssessmentsByQuery() {
    const userUid = ''; // this.officeService.office()?.author;
    const ref = collection(this.firestore, this.collectionName()) as CollectionReference<unknown>;
    // const qc: QueryConstraint[] = [where('author', '==', userUid), orderBy('name', 'desc'), limit(1)];
    const qc: QueryConstraint[] = [where('author', '==', userUid)];

    collectionData(query(ref, ...qc))
      .pipe(
        map(result => {
          return result.map(item => {
            console.log('Assessment item: ', item);
            const assessment: Assessment = { ...(item as Assessment) };
            return assessment;
          });
        })
      )
      .subscribe((result: Assessment[]) => {
        console.log('getAssessments result', result);
        this.assessmentList.set(result);
      });
  }
}
